export const SUPPORTED_BILLING_CURRENCIES = ['RUB', 'USD', 'KZT', 'UZS'] as const;
export const BILLING_MONEY_MINOR_UNITS_PER_MAJOR_UNIT = 100;
const BILLING_MONEY_FRACTION_DIGITS = 2;
const BILLING_TOKEN_FRACTION_DIGITS = 2;
const ROUNDING_DIGIT_OFFSET = BILLING_MONEY_FRACTION_DIGITS;
const MONEY_MAJOR_UNITS_PATTERN = /^(\d+)(?:\.(\d*))?$/;
const MAX_SAFE_MINOR_UNITS = BigInt(Number.MAX_SAFE_INTEGER);

export type BillingCurrency = (typeof SUPPORTED_BILLING_CURRENCIES)[number];

export const DEFAULT_BILLING_CURRENCY: BillingCurrency = 'RUB';

const SUPPORTED_BILLING_CURRENCY_SET = new Set<string>(SUPPORTED_BILLING_CURRENCIES);

export const normalizeBillingCurrency = (value: unknown): BillingCurrency | null => {
    if (typeof value !== 'string') {
        return null;
    }

    const normalized = value.trim().toUpperCase();
    return SUPPORTED_BILLING_CURRENCY_SET.has(normalized) ? (normalized as BillingCurrency) : null;
};

export const resolveBillingCurrency = (
    value: unknown,
    fallbackCurrency: BillingCurrency = DEFAULT_BILLING_CURRENCY,
): BillingCurrency => normalizeBillingCurrency(value) ?? fallbackCurrency;

export const formatSupportedBillingCurrencies = (): string => SUPPORTED_BILLING_CURRENCIES.join(', ');

export const formatInvalidBillingCurrencyMessage = (name: string, value: unknown): string =>
    `Invalid ${name}="${String(value)}". Allowed values: ${formatSupportedBillingCurrencies()}`;

export const requireBillingCurrency = (
    value: unknown,
    createErrorMessage: (value: unknown) => string = (rawValue) =>
        `Unsupported billing currency "${String(rawValue)}". Allowed values: ${formatSupportedBillingCurrencies()}`,
): BillingCurrency => {
    const normalizedCurrency = normalizeBillingCurrency(value);
    if (!normalizedCurrency) {
        throw new Error(createErrorMessage(value));
    }

    return normalizedCurrency;
};

export const isSupportedBillingCurrency = (value: unknown): value is BillingCurrency =>
    typeof value === 'string' && SUPPORTED_BILLING_CURRENCY_SET.has(value);

export const toMoneyMajorUnits = (minorUnits: number): number =>
    Number.isInteger(minorUnits) && minorUnits > 0 ? minorUnits / BILLING_MONEY_MINOR_UNITS_PER_MAJOR_UNIT : 0;

export const formatMoneyMajorUnitsInput = (minorUnits: number): string =>
    Number.isInteger(minorUnits) && minorUnits > 0
        ? (minorUnits / BILLING_MONEY_MINOR_UNITS_PER_MAJOR_UNIT).toFixed(BILLING_MONEY_FRACTION_DIGITS)
        : '';

export type BillingMoneyFormatOptions = {
    intlLocale: string;
    currency: BillingCurrency;
};

export const formatMoneyMinorUnits = (
    minorUnits: number,
    { intlLocale, currency }: BillingMoneyFormatOptions,
): string => new Intl.NumberFormat(intlLocale, { style: 'currency', currency }).format(toMoneyMajorUnits(minorUnits));

export type BillingTokenAmountFormatOptions = {
    intlLocale: string;
};

const normalizeBillingTokenDisplayAmount = (value: number): number => (Number.isFinite(value) ? value : 0);

export const formatBillingTokenAmount = (value: number, { intlLocale }: BillingTokenAmountFormatOptions): string =>
    new Intl.NumberFormat(intlLocale, {
        minimumFractionDigits: BILLING_TOKEN_FRACTION_DIGITS,
        maximumFractionDigits: BILLING_TOKEN_FRACTION_DIGITS,
    }).format(normalizeBillingTokenDisplayAmount(value));

const normalizeMajorUnitsInput = (majorUnits: number | string): string | null => {
    if (typeof majorUnits === 'number') {
        return Number.isFinite(majorUnits) && majorUnits > 0 ? majorUnits.toString() : null;
    }

    const trimmed = majorUnits.trim();
    return trimmed.length > 0 ? trimmed : null;
};

const toSafeMinorUnitsNumber = (minorUnits: bigint): number | null =>
    minorUnits <= MAX_SAFE_MINOR_UNITS ? Number(minorUnits) : null;

const getRoundedFractionalMinorUnits = (fractionalMajorUnits: string): bigint => {
    const paddedFraction = fractionalMajorUnits.padEnd(BILLING_MONEY_FRACTION_DIGITS + 1, '0');
    const minorUnitDigits = paddedFraction.slice(0, BILLING_MONEY_FRACTION_DIGITS);
    const roundingDigit = Number.parseInt(paddedFraction[ROUNDING_DIGIT_OFFSET] ?? '0', 10);

    return BigInt(minorUnitDigits) + (roundingDigit >= 5 ? 1n : 0n);
};

export const parseMoneyMinorUnits = (majorUnits: number | string): number | null => {
    const normalizedMajorUnits = normalizeMajorUnitsInput(majorUnits);
    if (!normalizedMajorUnits) {
        return null;
    }

    const match = MONEY_MAJOR_UNITS_PATTERN.exec(normalizedMajorUnits);
    if (!match) {
        return null;
    }

    const majorUnitValue = BigInt(match[1]);
    const fractionalMinorUnits = getRoundedFractionalMinorUnits(match[2] ?? '');
    const minorUnits = majorUnitValue * BigInt(BILLING_MONEY_MINOR_UNITS_PER_MAJOR_UNIT) + fractionalMinorUnits;

    return toSafeMinorUnitsNumber(minorUnits);
};

export const toMoneyMinorUnits = (majorUnits: number | string): number => parseMoneyMinorUnits(majorUnits) ?? 0;

const normalizeLocalizedMajorUnitsInput = (majorUnits: number | string): string | null => {
    const normalizedMajorUnits = normalizeMajorUnitsInput(majorUnits);
    if (!normalizedMajorUnits) {
        return null;
    }

    const decimalSeparatorCount = (normalizedMajorUnits.match(/[,.]/g) ?? []).length;
    if (decimalSeparatorCount > 1) {
        return null;
    }

    return normalizedMajorUnits.replace(',', '.');
};

export const parseLocalizedMoneyMinorUnits = (majorUnits: number | string): number | null => {
    const normalizedMajorUnits = normalizeLocalizedMajorUnitsInput(majorUnits);

    return normalizedMajorUnits ? parseMoneyMinorUnits(normalizedMajorUnits) : null;
};
